import {
  CardDateItemState,
  CardTypeItem,
  fetchRodLiftAnalysisCardTypesAsync,
} from '../../../features/rod-lift-analysis/CardDateSlice';
import { postGetCardAsync } from '../../../features/rod-lift-analysis/RodLiftAnalysisService';
import CardDateTable from '../../data-tables/CardDateTable';
import './CardTableView.scss';
import FullScreenIcon from '../../../features/common/FullScreenIcon/FullScreenIcon';
import { useEffect, useRef, useState } from 'react';
import { useAppSelector, useAppDispatch } from '../../../hooks/storeHooks';
import { useParams } from 'react-router-dom';
import ColumnSelector from '../../column-selector/colum-selector';
import { saveUserPerferences, DashboardConfig } from '../../../dispatchers/UserPreferenceDispatcher';
import { ALLY_CONFIGURATIONS } from '../../../services/configuration/config';
import Loader from '../../../features/common/Loader/Loader';
import { showErrorToast, showSuccessToast } from '../../toast/toastBridge';
import { ToastServiceProvider } from '../../toast/ToastService';

const CardTableView: React.FC<{
  cardDateItems: CardDateItemState[];
  rowSelectionClick: (index: number, date: string) => void;
  rowBactchSelectionClick: (datesToFetch: string[]) => void;
  cardTypeNames: string[];
  viewCardAnalysis: (rowData: CardDateItemState) => void;
}> = ({ cardDateItems, rowSelectionClick, rowBactchSelectionClick, viewCardAnalysis }) => {
  const dispatch = useAppDispatch();
  const { assetId } = useParams<{ assetId: string }>();

  // Fullscreen functionality
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Export functionality
  const [triggerExport, setTriggerExport] = useState(false);
  const exportDropdownRef = useRef<HTMLDivElement>(null);
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);
  const getCardsDropdownRef = useRef<HTMLDivElement>(null);
  const [isGetCardsDropdownOpen, setIsGetCardsDropdownOpen] = useState(false);

  // Column management state
  const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const [fixedColumns, setFixedColumns] = useState<string[]>([]);
  const [allColumnsForToolbar, setAllColumnsForToolbar] = useState<string[]>([]);
  const [applyTrigger, setApplyTrigger] = useState(false);
  const [isResetClicked, setIsResetClicked] = useState(false);
  const [isConfigCheckBoxClicked, setIsConfigCheckBoxClicked] = useState(false);
  const [taskWidgetProps, setTaskWidgetProps] = useState<{ columns?: any[]; fixedColumns?: any[] }>({});
  const [hasReset, setHasReset] = useState(false);
  const [hasColumnsReset, setHasColumnsReset] = useState(false);

  // Get widget configuration from Redux store
  const pumpCheckerPageData = useAppSelector((state: any) => state.testWellColumnsSlice.data?.widgets);
  const taskWidget = pumpCheckerPageData?.find((obj: any) => obj.key === 'cardViewer');
  const cardTypesData = useAppSelector((state: any) => state.rodLiftAnalysis?.cardTypes || []);
  const cardTypes = Array.isArray(cardTypesData) ? cardTypesData : cardTypesData?.values || [];
  const isLoadingCardTypes = useAppSelector((state: any) => state.rodLiftAnalysis?.isLoadingCardTypes || false);
  const socketId = useAppSelector((state: any) => state.assetGroups?.socketId);

  // Get chart type
  const chartType = useAppSelector((state: any) => state.AssetAnalysis?.chartType);
  const isRLDashboard = chartType?.toLowerCase().includes('rl');

  useEffect(() => {
    if (isRLDashboard && assetId && cardTypes.length === 0) {
      dispatch(fetchRodLiftAnalysisCardTypesAsync(assetId));
    }
  }, [isRLDashboard, assetId, cardTypes.length]);

  // Column definitions - this should match your CardDateTable columns
  const columnDefs = [
    { Header: '', accessor: 'isSelected' },
    { Header: '', accessor: 'color' },
    { Header: 'Date / time', accessor: 'date' },
    { Header: 'Card type', accessor: 'cardTypeName' },
    { Header: 'DH stroke', accessor: 'downHoleStroke' },
    { Header: 'Pump efficiency', accessor: 'pumpEfficiency' },
    { Header: 'Pump fillage', accessor: 'pumpFillage' },
    { Header: 'Pump fillage setpoint', accessor: 'pumpFillageSetpoint' },
    { Header: 'Secondary fillage setpoint', accessor: 'secondaryFillageSetpoint' },
    { Header: 'Inferred production', accessor: 'inferredProduction' },
    { Header: 'Runtime', accessor: 'runtime' },
    { Header: 'SPM', accessor: 'strokesPerMinute' },
    { Header: 'Min polished rod load', accessor: 'minPolishedRodLoad' },
    { Header: 'Max polished rod load', accessor: 'maxPolishedRodLoad' },
    { Header: 'Fluid load', accessor: 'fluidPound' },
    { Header: 'Buoyant rod weight', accessor: 'buoyantRodWeight' },
    { Header: 'Dry rod weight', accessor: 'dryRodWeight' },
    { Header: 'Pump friction load', accessor: 'pumpFriction' },
    { Header: 'PO fluid load', accessor: 'poFluidLoad' },
  ];

  // Initialize column state from widget configuration
  useEffect(() => {
    const props = taskWidget?.widgetProperty?.widgetProperties || {};
    setTaskWidgetProps(props);

    // Always include isSelected, color, and date as fixed columns (non-manageable)
    const mandatoryColumns = ['isSelected', 'color', 'date'];
    const fixed = [...mandatoryColumns, ...(props.fixedColumns?.map((col: any) => col.key).filter(Boolean) || [])];

    // Get visible columns from config, but ensure mandatory columns are always included
    const configuredVisible = props.columns?.filter((col: any) => col.selected).map((col: any) => col.key) || [];
    const visible = [
      ...mandatoryColumns,
      ...configuredVisible.filter((col: string) => !mandatoryColumns.includes(col)),
    ];

    // All columns for toolbar should exclude the mandatory columns (they won't appear in column selector)
    const allConfigurable =
      props.columns?.map((col: any) => col.key) ||
      columnDefs.filter((col) => !mandatoryColumns.includes(col.accessor)).map((col) => col.accessor);

    setFixedColumns(fixed);
    setVisibleColumns(visible);
    setAllColumnsForToolbar(allConfigurable);
    setApplyTrigger(true);
    setHasColumnsReset(true);
    setTimeout(() => setApplyTrigger(false), 300);
  }, [taskWidget, assetId]);

  useEffect(() => {
    setHasColumnsReset(true);
  }, [pumpCheckerPageData]);

  const getColumnHeader = (columnId: string) => {
    const col = columnDefs.find((col: any) => col.accessor === columnId);
    return col?.Header || columnId;
  };

  const resetHandler = () => {
    if (!taskWidgetProps?.columns?.length) return;

    setIsResetClicked(true);
    const mandatoryColumns = ['isSelected', 'color', 'date'];
    const configurableColumnsAfterReset = taskWidgetProps.columns
      .filter((col: any) => !mandatoryColumns.includes(col.key))
      .map((col: any) => col.key);
    const visibleColumnsAfterReset = [...mandatoryColumns, ...configurableColumnsAfterReset];

    setAllColumnsForToolbar(configurableColumnsAfterReset);
    setVisibleColumns(visibleColumnsAfterReset);

    setTimeout(() => {
      setIsResetClicked(false);
    }, 3000);
  };

  const handleApplyClicked = async () => {
    const mandatoryColumns = ['isSelected', 'color', 'date'];

    // Get currently selected columns from localColumns (which reflects current ColumnSelector state)
    const selectedColumns = localColumns.filter((col) => col.isVisible).map((col) => col.id);
    const newVisibleColumns = [
      ...mandatoryColumns,
      ...selectedColumns.filter((col) => !mandatoryColumns.includes(col)),
    ];

    // Update visible columns immediately to reflect changes in the table
    setVisibleColumns(newVisibleColumns);

    // Only include configurable columns in the transformation (exclude mandatory columns)
    const configurableColumns = localColumns.filter((item) => !mandatoryColumns.includes(item.id));
    const transformedArray = configurableColumns.map((item, index) => ({
      order: index + 1,
      label: item.Header,
      key: item.id,
      selected: item.isVisible,
    }));

    if (isConfigCheckBoxClicked) {
      try {
        const transformedCardViewerWidget: DashboardConfig = {
          DashboardName: taskWidget?.widgetProperty?.dashboard ?? '',
          WidgetName: taskWidget?.widgetProperty?.key ?? 'cardViewer',
          PropertyType: 'Columns',
          Preferences: transformedArray.map((col) => ({
            Order: col.order,
            Label: col.label,
            Key: col.key,
            Selected: col.selected,
            Visible: true,
          })),
        };

        const resultAction = await dispatch(saveUserPerferences(transformedCardViewerWidget));
        if (typeof resultAction.payload === 'boolean') {
          if (resultAction.payload) {
            showSuccessToast('Success', 'Configuration saved successfully.');
          } else {
            showErrorToast('Error', 'Failed to save configuration. Please try again.');
          }
        }
      } catch (error) {
        showErrorToast('Error', 'Error saving column configuration.');
      }
    }

    setApplyTrigger(true);
    setTimeout(() => setApplyTrigger(false), 300);
  };

  const handleSaveConfig = (config: boolean) => {
    setIsConfigCheckBoxClicked(config);
  };

  // Prepare columns for ColumnSelector
  const columnsTransferred: {
    id: string;
    Header: string | React.ReactNode;
    isVisible: boolean;
  }[] = allColumnsForToolbar
    .filter((id) => !fixedColumns.includes(id))
    .map((id) => ({
      id,
      Header: getColumnHeader(id),
      isVisible: visibleColumns.includes(id),
    }));

  const [localColumns, setLocalColumns] = useState(() =>
    columnsTransferred.map((col) => ({
      id: col.id,
      Header: typeof col.Header === 'string' ? col.Header : '',
      isVisible: col.isVisible,
      key: col.id,
    })),
  );

  useEffect(() => {
    if (hasReset || !localColumns.length || hasColumnsReset) {
      setLocalColumns(
        columnsTransferred.map((col) => ({
          id: col.id,
          Header: typeof col.Header === 'string' ? col.Header : '',
          isVisible: col.isVisible,
          key: col.id,
        })),
      );
      setHasReset(false);
      setHasColumnsReset(false);
    }
  }, [hasReset, columnsTransferred, isResetClicked, hasColumnsReset, assetId]);

  const toggleFullscreen = () => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Handle export dropdown click outside
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target as Node)) {
        setIsExportDropdownOpen(false);
      }
      if (getCardsDropdownRef.current && !getCardsDropdownRef.current.contains(event.target as Node)) {
        setIsGetCardsDropdownOpen(false);
      }
    };

    if (isExportDropdownOpen || isGetCardsDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExportDropdownOpen, isGetCardsDropdownOpen]);

  const handleExportXLS = () => {
    setIsExportDropdownOpen(false);
    setTriggerExport(true);
    setTimeout(() => setTriggerExport(false), 500);
  };

  // configurable limit from config
  const CARD_SELECTION_LIMIT = ALLY_CONFIGURATIONS.MAX_CARDS_SELECTABLE;
  // selected cards count
  const selectedCardsCount = cardDateItems.filter((item) => item.isSelected).length;

  const handleGetCardsClick = async () => {
    if (!assetId) return;

    if (cardTypes.length === 0 && !isGetCardsDropdownOpen) {
      await dispatch(fetchRodLiftAnalysisCardTypesAsync(assetId));
    }

    setIsGetCardsDropdownOpen(!isGetCardsDropdownOpen);
  };

  const handleCardTypeSelect = async (cardType: CardTypeItem) => {
    if (!assetId || !socketId) {
      console.error('Missing required data: assetId or socketId');
      return;
    }
    try {
      const payload = {
        assetId: assetId,
        cardType: cardType.cardTypeCode,
        socketId: socketId,
      };
      const response = await postGetCardAsync(payload);

      if (response.status >= 200 && response.status < 300) {
        showSuccessToast('Success', 'Get card request sent successfully. Processing...');
      } else {
        showErrorToast('Error', 'Failed to get card, please try again');
      }
    } catch (error) {
      showErrorToast('Error', 'Failed to get card, please try again');
    }

    setIsGetCardsDropdownOpen(false);
  };

  return (
    <div className={`card-table-wrapper ${isFullscreen ? 'fullscreen' : ''}`} ref={containerRef}>
      <ToastServiceProvider>
        <div className='card-header-wrap'>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div>Well status</div>
            {selectedCardsCount >= CARD_SELECTION_LIMIT && (
              <div className='selected-cards-msg'>{selectedCardsCount} cards selected</div>
            )}
          </div>
          <button onClick={toggleFullscreen}>
            <FullScreenIcon />
          </button>
        </div>
        <div className='card-header-divider' />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '10px 15px',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              className='flex w-[133px] h-[40px] pt-[10px] pr-[16px] pb-[10px] pl-[16px] gap-[8px] items-center shrink-0 flex-nowrap bg-[#131f2e] rounded-[8px] border-solid border border-[#4a5463] relative overflow-hidden shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] table-button'
              onClick={() => setIsColumnSelectorOpen(true)}
            >
              <span className='ico-colom' />
              <span className="h-[20px] shrink-0 basis-auto font-['Mulish'] text-[14px] font-bold leading-[20px] text-[#f7f9f9] relative text-left whitespace-nowrap">
                Columns
              </span>
            </button>
            <div className='relative' ref={exportDropdownRef}>
              <button
                className='flex w-[133px] h-[40px] pt-[10px] pr-[16px] pb-[10px] pl-[16px] gap-[8px] items-center shrink-0 flex-nowrap  rounded-[8px] border-solid border border-[#4a5463] relative overflow-hidden shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] table-button'
                onClick={() => setIsExportDropdownOpen(!isExportDropdownOpen)}
              >
                <span className='ico-down' />
                <span className="h-[20px] shrink-0 basis-auto font-['Mulish'] text-[14px] leading-[20px] text-[#f7f9f9] relative text-left whitespace-nowrap">
                  Export
                </span>
                <span className='down-chevron' />
              </button>
              {isExportDropdownOpen && (
                <div
                  className='absolute top-full left-0 mt-1 w-[160px] bg-[#131f2e] border border-[#4a5463] rounded-[8px] shadow-lg'
                  style={{ zIndex: 999999, width: '132px' }}
                >
                  <ul>
                    <li
                      className='px-4 py-3 text-[#f7f9f9] hover:bg-[#1c2a3b] cursor-pointer flex items-center gap-2'
                      onClick={() => {
                        handleExportXLS();
                      }}
                    >
                      <span className="font-['Mulish'] text-[14px]">Export XLS</span>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div className='relative' ref={getCardsDropdownRef}>
            <button
              className='flex w-[125px] h-[40px] pt-[10px] pr-[16px] pb-[10px] pl-[16px] gap-[8px] items-center shrink-0 flex-nowrap rounded-[8px] border-solid border border-[#4a5463] relative overflow-hidden shadow-[0_1px_2px_0_rgba(16,24,40,0.05)]'
              onClick={handleGetCardsClick}
              disabled={isLoadingCardTypes}
            >
              <span className='h-[20px] shrink-0 basis-auto text-[14px] font-bold leading-[20px] text-[#f7f9f9] relative text-left whitespace-nowrap'>
                Get cards
              </span>
              <span className='down-chevron' />
            </button>
            {isGetCardsDropdownOpen && (
              <div
                className='absolute top-full right-0 mt-1 w-[320px] bg-[#001023] border border-[#4a5463] rounded-[8px] shadow-lg max-h-[372px]'
                style={{ zIndex: 9999 }}
              >
                <div className="px-4 py-3 font-['Mulish'] text-[14px] border-b border-[#4a5463] text-[#CDD5DF]">
                  Get card
                </div>
                <div className='max-h-[240px] overflow-y-auto'>
                  {isLoadingCardTypes ? (
                    <div className='px-4 py-3 text-center'>
                      <Loader />
                    </div>
                  ) : cardTypes.length > 0 ? (
                    <ul>
                      {cardTypes.map((cardType: any, index: number) => (
                        <li
                          key={`${cardType.cardTypeCode}-${index}`}
                          className='px-4 py-3 text-[#f7f9f9] hover:bg-[#1c2a3b] cursor-pointer flex items-center gap-2'
                          onClick={() => handleCardTypeSelect(cardType)}
                        >
                          <span className="font-['Mulish'] text-[16px]" title={cardType.cardTypeDescription}>
                            {cardType.cardTypeDescription}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className='px-4 py-3 text-[#f7f9f9] text-center'>No card types available</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <CardDateTable
          cardDateItems={cardDateItems}
          rowSelectionClick={rowSelectionClick}
          rowBactchSelectionClick={rowBactchSelectionClick}
          triggerExport={triggerExport}
          viewCardAnalysis={viewCardAnalysis}
          visibleColumns={visibleColumns}
          applyTrigger={applyTrigger}
          isResetClicked={isResetClicked}
          isFullscreen={isFullscreen}
        />

        {/* Column Selector Modal */}
        {isColumnSelectorOpen && (
          <ColumnSelector
            columnsData={localColumns}
            onUpdateColumns={(updated) => {
              setLocalColumns(updated);
              // Do NOT call handleUpdateColumns here - this ensures changes are not immediately reflected
              // Changes will only be applied when the Apply button is clicked
            }}
            onClose={() => setIsColumnSelectorOpen(false)}
            onApplyClickedInColumn={handleApplyClicked}
            saveConfigHasbeenClicked={handleSaveConfig}
            resetToDefaultSystemClicked={() => {
              resetHandler();
              setHasReset(true);
            }}
            tableName='cardViewer'
            isChartConfig={false}
          />
        )}
      </ToastServiceProvider>
    </div>
  );
};

export default CardTableView;
